import React, { useState, useCallback, useEffect, useRef } from 'react'
import { Button, Input, message } from 'antd'
import {
  CheckOutlined,
  RightOutlined,
  SearchOutlined,
  UserOutlined
} from '@ant-design/icons'
import { useAppDispatch } from '../../../app/hooks'
import {
  fetchInviteStatus,
  searchGroupByName,
  joinGroupApply
} from '../../../actions/main'
import sensors from '../../../utils/sensors'
import { capturePromiseException } from '../../../utils/sentryUtils'
import todaycam_logo from '../../../images/todaycam_logo.png'
import './index.scss'

type Step =
  | 'method'
  | 'byId'
  | 'bySearch'
  | 'detail'
  | 'submitted'
  | 'joinedDirect'

interface SearchGroupItem {
  groupID: string
  groupIDReal: string
  groupName: string
  groupColor: string
  industryName: string
  adminName: string
  canEnterGroup: boolean
  avatar: string
}

interface InviteStatusResult {
  status: number
  group_id: string
  invite_id: string
  group_name: string
  group_user_num: number
  creator: string
  headimgurl_list: string[]
  needApply: boolean
}

interface JoinGroupProps {
  onClose: () => void
  onGotoCreate: () => void
  fromPlace?: string
}

const JoinGroup: React.FC<JoinGroupProps> = ({
  onClose,
  onGotoCreate,
  fromPlace
}) => {
  const dispatch = useAppDispatch()
  const [step, setStep] = useState<Step>('method')

  const [idInput, setIdInput] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [searchResults, setSearchResults] = useState<SearchGroupItem[]>([])
  const [searchSubState, setSearchSubState] = useState<
    'idle' | 'list' | 'tooMany' | 'rateLimit'
  >('idle')

  const [detail, setDetail] = useState<InviteStatusResult | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const trackPlace = fromPlace || 'webTopJoinGuide'

  // detail 是从 byId 还是 bySearch 进入的；上报 page 时需要
  const lastSearchPageRef = useRef<'searchGroupID' | 'searchGroupName'>(
    'searchGroupID'
  )
  // 进入 detail / submitted 时仍要上报触发搜索的关键字
  const lastSearchKeywordRef = useRef<string>('')
  // 跟踪当前 step，用于 unmount 时获取最后状态
  const currentStepRef = useRef<Step>('method')
  // 标记 close 已被显式 clickItem 替代（如 submitOver / notJoinClose），unmount 时不再补 close
  const closeTrackedRef = useRef(false)
  // 同关键字反复搜索无结果时引导用户改用团队号
  const sameKeywordEmptyCountRef = useRef(0)
  const lastEmptyKeywordRef = useRef('')
  const [emptyRetry, setEmptyRetry] = useState(false)

  useEffect(() => {
    currentStepRef.current = step
  }, [step])

  const stepToPage = (s: Step): string => {
    switch (s) {
      case 'method':
        return 'chooseWay'
      case 'byId':
        return 'searchGroupID'
      case 'bySearch':
        return 'searchGroupName'
      case 'submitted':
        return 'submit'
      case 'detail':
        return lastSearchPageRef.current
      default:
        return ''
    }
  }

  // 统一上报 workgroup_b8110_click。extras 可覆盖 page / 携带额外字段
  const trackClick = (
    clickItem: string,
    extras: Record<string, any> = {},
    pageOverride?: string
  ) => {
    sensors.track('workgroup_b8110_click', {
      isLogin: true,
      page: pageOverride || stepToPage(currentStepRef.current),
      fromPlace: trackPlace,
      clickItem,
      track_sign: 'aworkgroup.b8110.click.workgroup_b8110_click',
      requirement_id: 1090,
      ...extras
    })
  }

  useEffect(() => {
    sensors.track('workgroup_b8110_enter', {
      fromPlace: trackPlace,
      track_sign: 'aworkgroup.b8110.enter.workgroup_b8110_enter',
      isLogin: true,
      requirement_id: 1090
    })
    return () => {
      // 关闭弹窗（X / 蒙层 / ESC）兜底上报 close；显式 close 已通过 closeTrackedRef 屏蔽
      if (!closeTrackedRef.current) {
        trackClick('close')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const queryInviteStatus = useCallback(
    (inviteId: string) => {
      if (!inviteId) {
        message.warning('请输入团队号')
        return
      }
      dispatch(fetchInviteStatus(inviteId))
        .then((data: InviteStatusResult) => {
          if (data.status === 0) {
            setDetail(data)
            setStep('detail')
          } else if (data.status === -2) {
            message.error('团队不存在，请确认团队号是否正确')
          } else {
            message.error('查询失败，请稍后再试')
          }
        })
        .catch(capturePromiseException)
    },
    [dispatch]
  )

  const handleSearchByName = useCallback(() => {
    const name = searchInput.trim()
    if (name.length < 3) {
      message.warning('团队名至少输入3个字')
      return
    }
    dispatch(searchGroupByName(name))
      .then((data: { status: number; groups: SearchGroupItem[] }) => {
        if (data.status === 0) {
          const groups = data.groups || []
          setSearchResults(groups)
          setSearchSubState('list')
          // 同关键字反复无结果 → 第 2 次起切到引导卡
          if (groups.length === 0) {
            if (lastEmptyKeywordRef.current === name) {
              sameKeywordEmptyCountRef.current += 1
            } else {
              sameKeywordEmptyCountRef.current = 1
              lastEmptyKeywordRef.current = name
            }
            setEmptyRetry(sameKeywordEmptyCountRef.current >= 2)
          } else {
            sameKeywordEmptyCountRef.current = 0
            lastEmptyKeywordRef.current = ''
            setEmptyRetry(false)
          }
          trackClick(
            'showGroupList',
            {
              searchKeyword: name,
              groupIDList: groups.map((g) => g.groupIDReal || g.groupID)
            },
            'searchGroupName'
          )
        } else if (data.status === 1) {
          message.warning('团队名长度需在 3~16 个字之间')
        } else if (data.status === 2) {
          setSearchResults([])
          setSearchSubState('tooMany')
          trackClick('noResultShow', { searchKeyword: name }, 'searchGroupName')
        } else if (data.status === 3) {
          setSearchResults([])
          setSearchSubState('rateLimit')
          trackClick('notJoinShow', { searchKeyword: name }, 'searchGroupName')
        } else {
          message.error('搜索失败，请稍后再试')
        }
      })
      .catch(capturePromiseException)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, dispatch])

  const handleJoinFromList = (item: SearchGroupItem) => {
    if (!item.canEnterGroup) {
      message.warning('今日加入团队次数已用完，请明天再试')
      return
    }
    trackClick(
      'joinOneGroup',
      {
        searchKeyword: searchInput.trim(),
        groupID: item.groupID,
        groupName: item.groupName
      },
      'searchGroupName'
    )
    lastSearchPageRef.current = 'searchGroupName'
    lastSearchKeywordRef.current = searchInput.trim()
    // invite/status 接收的是团队号（短 ID），即搜索结果的 groupID，不是 groupIDReal
    queryInviteStatus(item.groupID)
  }

  const handleApply = () => {
    if (!detail) return
    setSubmitting(true)
    // source: 通过 ID 搜索还是名称搜索进来的，便于后端分析转化路径
    const source =
      lastSearchPageRef.current === 'searchGroupID'
        ? 'webSearchIdAddGroup'
        : 'webSearchGroupName'
    dispatch(joinGroupApply(detail.group_id, 0, source))
      .then((data: { status: number; msg?: string }) => {
        sensors.track('web_workgroup_join_apply', {
          groupID: detail.group_id,
          fromPlace: trackPlace,
          needApply: detail.needApply,
          status: data && data.status
        })
        if (!data) {
          message.error('提交失败，请稍后再试')
          return
        }
        switch (data.status) {
          case 0:
            message.success('已加入团队')
            window.location.reload()
            return
          case 100:
            setStep('submitted')
            return
          case -2:
            message.error('团队不存在')
            return
          case -66:
            message.warning('您加入的团队数已达上限')
            return
          case 41001:
            message.warning(data.msg || '当前团队已达到成员上限，请联系管理员')
            return
          case 400:
            message.error(data.msg || '入参错误')
            return
          case -500:
          default:
            message.error(data.msg || '提交失败，请稍后再试')
        }
      })
      .catch(capturePromiseException)
      .finally(() => setSubmitting(false))
  }

  const handleSearchById = () => {
    const kw = idInput.trim()
    trackClick('search', { searchKeyword: kw }, 'searchGroupID')
    lastSearchPageRef.current = 'searchGroupID'
    lastSearchKeywordRef.current = kw
    queryInviteStatus(kw)
  }

  const handleSearchByNameClick = () => {
    const kw = searchInput.trim()
    trackClick('search', { searchKeyword: kw }, 'searchGroupName')
    handleSearchByName()
  }

  const handleGotoCreate = () => {
    trackClick('toCreate')
    closeTrackedRef.current = true
    onGotoCreate()
  }

  const handleApplyClick = () => {
    if (!detail) return
    trackClick('joinOneGroup', {
      searchKeyword: lastSearchKeywordRef.current,
      groupID: detail.group_id,
      groupName: detail.group_name
    })
    handleApply()
  }

  const handleSubmitOver = () => {
    trackClick('submitOver', {
      searchKeyword: lastSearchKeywordRef.current,
      groupID: detail?.group_id || '',
      groupName: detail?.group_name || ''
    })
    closeTrackedRef.current = true
    onClose()
  }

  const handleNoResultToGroupID = () => {
    trackClick(
      'noResultToGroupID',
      { searchKeyword: searchInput.trim() },
      'searchGroupName'
    )
    setStep('byId')
  }

  const handleNoResultContinue = () => {
    trackClick(
      'noResultContinue',
      { searchKeyword: searchInput.trim() },
      'searchGroupName'
    )
    setSearchSubState('idle')
  }

  const handleNotJoinClose = () => {
    trackClick(
      'notJoinClose',
      { searchKeyword: searchInput.trim() },
      'searchGroupName'
    )
    closeTrackedRef.current = true
    onClose()
  }

  const handleNotJoinToGroupID = () => {
    trackClick(
      'notJoinToGroupID',
      { searchKeyword: searchInput.trim() },
      'searchGroupName'
    )
    setStep('byId')
  }

  const renderHeader = (showLogo = true) => (
    <div className="jg-header">
      {showLogo && (
        <img className="jg-logo" src={todaycam_logo} alt="今日水印相机" />
      )}
      <div className="jg-title">加入团队</div>
    </div>
  )

  const renderFooterCreate = (variant?: 'method') => (
    <div
      className={`jg-footer${variant === 'method' ? ' jg-footer--method' : ''}`}
    >
      没有团队？
      <a className="jg-footer-link" onClick={handleGotoCreate}>
        去创建
      </a>
    </div>
  )

  if (step === 'method') {
    return (
      <div className="join-group">
        <div className="jg-method-title">加入团队</div>
        <div
          className="jg-method-item"
          onClick={() => {
            trackClick('chooseGroupID')
            setStep('byId')
            setIdInput('')
          }}
        >
          <span className="jg-method-icon jg-method-icon--id">123</span>
          <span className="jg-method-label">输入团队号加入</span>
          <RightOutlined className="jg-method-arrow" />
        </div>
        <div className="jg-method-divider" />
        <div
          className="jg-method-item"
          onClick={() => {
            trackClick('chooseGroupName')
            setStep('bySearch')
            setSearchInput('')
            setSearchResults([])
            setSearchSubState('idle')
          }}
        >
          <span className="jg-method-icon jg-method-icon--name">
            <SearchOutlined />
          </span>
          <span className="jg-method-label">搜索团队名称加入</span>
          <RightOutlined className="jg-method-arrow" />
        </div>
        <div className="jg-method-divider" />
        {renderFooterCreate('method')}
      </div>
    )
  }

  if (step === 'byId') {
    return (
      <div className="join-group">
        {renderHeader()}
        <div className="jg-search-row">
          <Input
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            placeholder="输入团队号"
            value={idInput}
            onChange={(e) => setIdInput(e.target.value)}
            onPressEnter={handleSearchById}
          />
          <span className="jg-search-action" onClick={handleSearchById}>
            搜索
          </span>
        </div>
        <div className="jg-tip">*团队号可向管理员或团队成员询问</div>
        {renderFooterCreate()}
      </div>
    )
  }

  if (step === 'bySearch') {
    return (
      <div className="join-group">
        {renderHeader()}
        <div className="jg-search-row">
          <Input
            prefix={
              <SearchOutlined
                style={{ color: '#999', fontSize: '16px', fontWeight: 600 }}
              />
            }
            placeholder="输入团队名"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onPressEnter={handleSearchByNameClick}
          />
          <span className="jg-search-action" onClick={handleSearchByNameClick}>
            搜索
          </span>
        </div>
        {searchSubState === 'idle' && (
          <div className="jg-tip">
            *团队号可向管理员或团队成员询问
            <br />
            团队名至少输入3个字
          </div>
        )}
        {searchSubState === 'list' && emptyRetry && (
          <div className="jg-card jg-card--center">
            <div className="jg-card-title">没找到团队</div>
            <div className="jg-card-desc">
              多次搜索仍无结果，可能是团队名不准确
              <br />
              建议直接通过团队号加入
            </div>
            <div className="jg-card-actions">
              <Button
                type="primary"
                onClick={() => {
                  sameKeywordEmptyCountRef.current = 0
                  lastEmptyKeywordRef.current = ''
                  setEmptyRetry(false)
                  setStep('byId')
                }}
              >
                输入团队号
              </Button>
            </div>
          </div>
        )}
        {searchSubState === 'list' && !emptyRetry && (
          <div className="jg-result-list">
            {searchResults.length === 0 ? (
              <div className="jg-empty">未搜索到相关团队</div>
            ) : (
              searchResults.map((item) => (
                <div className="jg-result-item" key={item.groupIDReal}>
                  <div
                    className="jg-result-avatar"
                    style={{ background: item.groupColor || '#19a0fd' }}
                  >
                    {item.avatar ? (
                      <img src={item.avatar} alt="" />
                    ) : (
                      <span>{(item.groupName || '').slice(0, 2)}</span>
                    )}
                  </div>
                  <div className="jg-result-info">
                    <div className="jg-result-name">{item.groupName}</div>
                    <div className="jg-result-meta">
                      <UserOutlined className="jg-meta-icon" />
                      <span className="jg-meta-admin">{item.adminName}</span>
                      {item.industryName && (
                        <span className="jg-meta-industry">
                          {item.industryName}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    className="jg-result-join"
                    onClick={() => handleJoinFromList(item)}
                  >
                    加入
                  </Button>
                </div>
              ))
            )}
          </div>
        )}
        {searchSubState === 'tooMany' && (
          <div className="jg-card jg-card--center">
            <div className="jg-card-title">无法显示结果</div>
            <div className="jg-card-desc">
              搜索结果存在大量同名团队，请尝试输入
              <span className="jg-card-link">团队全称</span>
              进行查询或者输入团队号加入
            </div>
            <div className="jg-card-actions">
              <Button onClick={handleNoResultToGroupID}>输入团队号</Button>
              <Button type="primary" onClick={handleNoResultContinue}>
                继续输入
              </Button>
            </div>
          </div>
        )}
        {searchSubState === 'rateLimit' && (
          <div className="jg-card jg-card--center">
            <div className="jg-card-title">无法加入团队</div>
            <div className="jg-card-desc">
              今日搜索加入团队过于频繁
              <br />
              您可以通过输入团队号进行加入团队
            </div>
            <div className="jg-card-actions">
              <Button onClick={handleNotJoinClose}>关闭</Button>
              <Button type="primary" onClick={handleNotJoinToGroupID}>
                输入团队号
              </Button>
            </div>
          </div>
        )}
        {searchSubState === 'idle' && renderFooterCreate()}
      </div>
    )
  }

  if (step === 'detail' && detail) {
    return (
      <div className="join-group">
        {renderHeader()}
        <div className="jg-detail-card">
          <div className="jg-detail-name">{detail.group_name}</div>
          <div className="jg-detail-id">团队号：{detail.invite_id}</div>
          {detail.headimgurl_list && detail.headimgurl_list.length > 0 && (
            <div className="jg-detail-avatars">
              {detail.headimgurl_list.slice(0, 8).map((url, idx) => (
                <img
                  key={`${url}-${idx}`}
                  src={url}
                  alt=""
                  className="jg-detail-avatar"
                />
              ))}
            </div>
          )}
          <Button
            type="primary"
            className="jg-detail-apply"
            loading={submitting}
            onClick={handleApplyClick}
          >
            {detail.needApply ? '申请加入' : '加入'}
          </Button>
        </div>
      </div>
    )
  }

  if (step === 'submitted') {
    return (
      <div className="join-group">
        <div className="jg-submitted">
          <div className="jg-submitted-icon">
            <CheckOutlined />
          </div>
          <div className="jg-submitted-title">申请已提交</div>
          <div className="jg-submitted-desc">正在等待管理员审核</div>
          <Button
            type="primary"
            className="jg-submitted-btn"
            onClick={handleSubmitOver}
          >
            确定
          </Button>
        </div>
      </div>
    )
  }

  return null
}

export default JoinGroup
